import React, { useCallback, useEffect, useState } from 'react';
import { getProducts } from '../../lib/api';
import { adminCreateProduct, adminUpdateProduct, adminDeleteProduct } from '../../lib/adminApi';
import { Plus, Pencil, Trash2, Search, X, Loader2, ImageIcon } from 'lucide-react';

const emptyForm = {
  name: '', slug: '', price: '', originalPrice: '', image: '',
  category: 'Research Peptides', tags: [], inStock: true, description: '',
};

const CATEGORY_OPTIONS = ['Research Peptides', 'Featured Blends', 'Supplements', 'Research Topicals', 'Accessories'];
const TAG_OPTIONS = ['featured', 'sale'];

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    getProducts({ sort: 'latest' })
      .then(setProducts)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setEditorOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setForm({
      name: product.name,
      slug: product.slug,
      price: product.price,
      originalPrice: product.originalPrice || '',
      image: product.image,
      category: product.category,
      tags: product.tags || [],
      inStock: product.inStock,
      description: product.description || '',
    });
    setError('');
    setEditorOpen(true);
  };

  const toggleTag = (tag) => {
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(tag) ? f.tags.filter((t) => t !== tag) : [...f.tags, tag],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        slug: form.slug || slugify(form.name),
        price: parseFloat(form.price),
        originalPrice: form.originalPrice ? parseFloat(form.originalPrice) : null,
        image: form.image,
        category: form.category,
        tags: form.tags,
        inStock: form.inStock,
        description: form.description || null,
      };
      if (editing) {
        await adminUpdateProduct(editing.id, payload);
      } else {
        await adminCreateProduct(payload);
      }
      setEditorOpen(false);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    try {
      await adminDeleteProduct(product.id);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const inputCls = 'w-full bg-zinc-950 border border-zinc-800 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-[#909090]';

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Products</h1>
          <p className="text-zinc-500 text-sm mt-1">{products.length} total items</p>
        </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold px-5 py-2.5 rounded transition-colors">
          <Plus size={18}/> Add product
        </button>
      </div>

      <div className="relative mb-5 max-w-md">
        <Search size={16} className="absolute top-1/2 left-3 -translate-y-1/2 text-zinc-500"/>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products..." className={`${inputCls} pl-9`} />
      </div>

      {loading ? (
        <div className="py-20 text-center text-zinc-500">Loading...</div>
      ) : (
        <div className="border border-zinc-900 rounded-lg overflow-hidden bg-zinc-950">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/50 text-zinc-400 uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left py-3 px-4">Image</th>
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-left py-3 px-4 hidden md:table-cell">Category</th>
                <th className="text-right py-3 px-4">Price</th>
                <th className="text-center py-3 px-4 hidden md:table-cell">Stock</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-zinc-900/30">
                  <td className="py-2 px-4">
                    <img src={p.image} alt={p.name} className="w-12 h-12 object-contain bg-black rounded" />
                  </td>
                  <td className="py-2 px-4 text-white">
                    <p className="font-medium">{p.name}</p>
                    <p className="text-xs text-zinc-500">{p.slug}</p>
                  </td>
                  <td className="py-2 px-4 text-zinc-400 hidden md:table-cell">{p.category}</td>
                  <td className="py-2 px-4 text-right text-[#909090] font-semibold">
                    {p.originalPrice && <span className="text-zinc-500 line-through mr-2 text-xs">€ {p.originalPrice.toFixed(2)}</span>}
                    € {p.price.toFixed(2)}
                  </td>
                  <td className="py-2 px-4 text-center hidden md:table-cell">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${p.inStock ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                      {p.inStock ? 'In stock' : 'Out'}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(p)} className="p-2 text-zinc-400 hover:text-white" aria-label="Edit"><Pencil size={16}/></button>
                    <button onClick={() => handleDelete(p)} className="p-2 text-zinc-400 hover:text-red-400" aria-label="Delete"><Trash2 size={16}/></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-zinc-500">No products found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editorOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 overflow-y-auto py-10">
          <form onSubmit={handleSave} className="bg-zinc-950 border border-zinc-800 rounded-lg w-full max-w-2xl p-6 my-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">{editing ? 'Edit product' : 'Add product'}</h2>
              <button type="button" onClick={() => setEditorOpen(false)} className="text-zinc-400 hover:text-white"><X size={20}/></button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded">{error}</div>}

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Name</label>
                <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value, slug: editing ? form.slug : slugify(e.target.value)})} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Slug (URL)</label>
                <input required value={form.slug} onChange={(e) => setForm({...form, slug: slugify(e.target.value)})} className={inputCls} />
              </div>
              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Price (€)</label>
                <input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} className={inputCls} />
              </div>
              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Original price (optional)</label>
                <input type="number" step="0.01" min="0" value={form.originalPrice} onChange={(e) => setForm({...form, originalPrice: e.target.value})} className={inputCls} placeholder="For sale display" />
              </div>
              <div className="col-span-2">
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Image URL</label>
                <div className="flex gap-3">
                  <input required value={form.image} onChange={(e) => setForm({...form, image: e.target.value})} className={inputCls} placeholder="https://..." />
                  {form.image ? (
                    <img src={form.image} alt="preview" className="w-12 h-12 object-contain bg-black rounded border border-zinc-800" />
                  ) : (
                    <div className="w-12 h-12 flex items-center justify-center bg-black rounded border border-zinc-800"><ImageIcon size={18} className="text-zinc-600"/></div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Category</label>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className={inputCls}>
                  {CATEGORY_OPTIONS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-4 pt-6">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.inStock} onChange={(e) => setForm({...form, inStock: e.target.checked})} className="accent-[#909090] w-4 h-4" />
                  <span className="text-sm text-zinc-300">In stock</span>
                </label>
              </div>
              <div className="col-span-2">
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-2">Tags</label>
                <div className="flex gap-2 flex-wrap">
                  {TAG_OPTIONS.map((t) => (
                    <button key={t} type="button" onClick={() => toggleTag(t)} className={`px-3 py-1.5 rounded border text-sm transition-colors ${form.tags.includes(t) ? 'bg-[#909090] border-[#909090] text-black' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'}`}>
                      {t}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-zinc-600 mt-2"><span className="text-zinc-400">featured</span> shows on homepage · <span className="text-zinc-400">sale</span> shows sale badge</p>
              </div>
              <div className="col-span-2">
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Description</label>
                <textarea
                  data-testid="admin-product-description-input"
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  rows={6}
                  className={`${inputCls} resize-y font-mono text-xs leading-relaxed`}
                  placeholder="Product description shown on the product page. Line breaks are preserved. Leave blank to fall back to the generic default."
                />
                <p className="text-xs text-zinc-600 mt-2">Displayed under the <span className="text-zinc-400">Description</span> tab on the product page. Blank = generic fallback text.</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-zinc-900">
              <button type="button" onClick={() => setEditorOpen(false)} className="px-5 py-2.5 text-zinc-400 hover:text-white">Cancel</button>
              <button disabled={saving} className="inline-flex items-center gap-2 bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold px-6 py-2.5 rounded transition-colors disabled:opacity-60">
                {saving ? <><Loader2 className="animate-spin" size={16}/> Saving...</> : (editing ? 'Save changes' : 'Create product')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
